/**
 * 🔴 **The ONE place the currency module reads the store.** The plain functions call these with
 * `store.getState()` and the hooks call them through `useSelector`, so the two can never disagree
 * about what the merchant's money looks like. Pure: a selector takes the state and touches nothing else.
 */
import { createSelector } from "@reduxjs/toolkit";

import { DEFAULT_MONEY_SETTINGS } from "./format";
import type { CommaStyle, MoneySettings } from "./format";
import { currencySymbolFor } from "./symbol";

/** The part of the app state the currency module reads: the business this terminal is bound to. */
export interface StateWithBusiness {
  businessStore?: { businessData?: { currencyCode?: string } };
}

/**
 * The business's currency CODE (`"INR"`, `"USD"`), or `undefined` before business data loads.
 * Use `selectMoneySettings` for anything printed; this is for code that needs the code itself.
 */
export function selectCurrencyCode(state: StateWithBusiness): string | undefined {
  return state?.businessStore?.businessData?.currencyCode;
}

/**
 * 🔴 **THE TWO LINES THE MERCHANT CONFIG LANDS ON.** Both answer the default today, because no
 * field on `businessData` carries a precision or a comma style yet. The day the backend ships them:
 *
 * ```ts
 * const selectAmountDecimals = (state) =>
 *   amountDecimalsFor(state?.businessStore?.businessData?.currencyDecimalPlaces);
 * const selectCommaStyle = (state) =>
 *   commaStyleFor(state?.businessStore?.businessData?.commaStyle);
 * ```
 *
 * and every price drawn through this module follows, hooks re-rendering included. Both go through
 * their guard (`amountDecimalsFor`, `commaStyleFor`), never `||`, which turns a configured `0` into
 * two. ⚠️ Both field names are guesses at the contract: confirm with the backend before wiring.
 */
const selectAmountDecimals = (): number => DEFAULT_MONEY_SETTINGS.decimals;

/** See `selectAmountDecimals` above: the same seam, for where the commas go. */
const selectCommaStyle = (): CommaStyle => DEFAULT_MONEY_SETTINGS.commaStyle;

/**
 * This merchant's `MoneySettings`: symbol, decimals and comma style, read together.
 *
 * Memoised: it hands back the SAME object until one of the three changes, which is what lets
 * `useMoneySettings()` re-render a component only when the money format really changed.
 *
 * @example selectMoneySettings(state) // { symbol: "₹", decimals: 2, commaStyle: "lakhs" }
 */
export const selectMoneySettings = createSelector(
  [selectCurrencyCode, selectAmountDecimals, selectCommaStyle],
  (currencyCode, decimals, commaStyle): MoneySettings => ({
    symbol: currencySymbolFor(currencyCode),
    decimals,
    commaStyle,
  }),
);
